import { getRoomBus } from "./audioBus";
import { getAudioContext } from "./getAudioContext";
import { getNoiseBuffer } from "./machineSynth";

/**
 * The hand saw's voice, fully synthesized like the machine voices in
 * `machineSynth.ts` — but where a machine derives everything from one
 * `rpm` scalar, the saw derives everything from the player's hand:
 * stroke speed, stroke direction, and how deep the kerf has gotten.
 * The bench surface feeds those in per pointer stroke and ticks the
 * voice every frame; nothing here loops a recording, so the sound is
 * exactly as rhythmic, hesitant, or frantic as the hand driving it.
 *
 *  TEETH — the signature buzz. Noise amplitude-modulated by a square
 *    wave whose frequency is the tooth-pass rate: teeth-per-inch times
 *    the hand's speed. Within each stroke the hand accelerates and
 *    decelerates, so the rate sweeps up and back down — the vvvVVVvvv
 *    contour of a real saw stroke, generated by the actual gesture.
 *    (The per-inch figure is tuned for where the buzz sounds right at
 *    bench pointer speeds, not lifted from a catalog blade.)
 *
 *  FIBER — broadband tearing under the buzz: the chopped noise through
 *    a bandpass whose center and level both track speed, so a lazy
 *    draw hisses low and a hard stroke rips bright.
 *
 *  BLADE — the plate's metallic shimmer: a detuned sine pair up around
 *    2.4 kHz that only sings at speed (quadratic level), the tell that
 *    separates steel in wood from sandpaper on it.
 *
 *  BODY — wood formants the chopped noise rings through, and a master
 *    lowpass that closes as the kerf deepens: a buried blade sounds
 *    duller and more wooden than one skating on the surface, so the
 *    cut audibly matures from scratchy to full-throated to muffled.
 *
 *  RATTLE — over the last strokes the offcut is barely held: a slow
 *    square-chopped burr around 500 Hz fades in past ~80% depth, the
 *    nearly-free piece buzzing against the blade. It telegraphs the
 *    end the same way the visible sag does.
 *
 * Push and pull are asymmetric (a western saw cuts on the push): one
 * direction plays louder and brighter, and each reversal fires a small
 * unload-and-rebite tick, so scrubbing back and forth reads as
 * push–pull rather than static.
 *
 * The parting crack lives in {@link playSawPartCrack}, detached from
 * the voice's graph so completion (which unmounts the surface and
 * disposes the voice) can't clip its tail.
 *
 * Like every voice: best-effort. Any Web Audio failure silences it
 * without throwing.
 */

/** Tooth-pass rate per inch of stroke travel (Hz per in/s). */
const TOOTH_RATE_PER_IN = 25;
/** Stroke speed (in/s) that counts as a full-effort stroke. */
const FULL_SPEED = 10;
/** Seconds for the speed envelope to decay once strokes stop. */
const SPEED_DECAY_TAU = 0.12;
/** How much quieter the return stroke is than the cutting stroke. */
const PULL_LEVEL = 0.6;

interface SawGraph {
  readonly master: GainNode;
  readonly lowpass: BiquadFilterNode;
  readonly toothOsc: OscillatorNode;
  readonly chopBase: GainNode;
  readonly chopDepth: GainNode;
  readonly fiberBandpass: BiquadFilterNode;
  readonly fiberGain: GainNode;
  readonly bodyGain: GainNode;
  readonly bladeGain: GainNode;
  readonly rattleGain: GainNode;
  readonly sources: ReadonlyArray<AudioScheduledSourceNode>;
}

export class HandSawVoice {
  private graph: SawGraph | null = null;
  private failed = false;
  private disposed = false;

  /** Smoothed stroke speed, in/s — bumped by strokes, decayed by ticks. */
  private speed = 0;
  private direction: 1 | -1 = 1;
  private depth = 0;

  /** A qualifying stroke event: how fast the hand moved, which way. */
  stroke(speedInPerS: number, direction: 1 | -1, depth: number): void {
    if (this.disposed || this.failed) return;
    try {
      if (!this.graph) this.graph = this.build();
      const speed = Math.min(30, Math.max(0, speedInPerS));
      if (direction !== this.direction && this.speed > 2) {
        this.reversalTick();
      }
      this.direction = direction;
      this.speed = Math.max(this.speed, speed);
      this.depth = Math.min(1, Math.max(0, depth));
    } catch {
      this.failed = true;
    }
  }

  /** Frame driver: decay the speed envelope and chase the targets. */
  tick(dtSeconds: number): void {
    if (this.disposed || this.failed || !this.graph) return;
    try {
      this.speed *= Math.exp(-dtSeconds / SPEED_DECAY_TAU);
      if (this.speed < 0.05) this.speed = 0;
      const g = this.graph;
      const ctx = getAudioContext();
      const now = ctx.currentTime;
      const chase = (param: AudioParam, value: number, tau = 0.045) =>
        param.setTargetAtTime(value, now, tau);

      const norm = Math.min(1, this.speed / FULL_SPEED);
      const level = Math.pow(norm, 1.3);
      const push = this.direction > 0;
      const effort = level * (push ? 1 : PULL_LEVEL);

      // TEETH: the buzz rate IS the hand speed
      chase(g.toothOsc.frequency, TOOTH_RATE_PER_IN * this.speed, 0.03);
      const chopDepth = 0.4 + 0.15 * this.depth;
      chase(g.chopDepth.gain, chopDepth);
      chase(g.chopBase.gain, 1 - chopDepth);

      // FIBER: louder and brighter the harder the stroke
      chase(g.fiberGain.gain, 0.5 * effort, 0.03);
      chase(
        g.fiberBandpass.frequency,
        900 + 1700 * norm * (push ? 1.15 : 1),
        0.06,
      );

      // BODY rises as the blade buries; the lowpass closes over it
      chase(g.bodyGain.gain, 0.9 * effort * (0.25 + 0.75 * this.depth));
      chase(g.lowpass.frequency, 6500 - 4300 * this.depth, 0.1);

      // BLADE only sings at speed
      chase(g.bladeGain.gain, 0.05 * norm * norm * (push ? 1 : 0.5));

      // RATTLE: the offcut coming loose over the last strokes
      const loose = Math.max(0, (this.depth - 0.8) / 0.2);
      chase(g.rattleGain.gain, 1.1 * level * loose * loose);
    } catch {
      this.failed = true;
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    try {
      if (this.graph) {
        const ctx = getAudioContext();
        this.graph.master.gain.setTargetAtTime(0, ctx.currentTime, 0.03);
        const graph = this.graph;
        this.graph = null;
        graph.sources.forEach((s) => s.stop(ctx.currentTime + 0.2));
        setTimeout(() => {
          try {
            graph.master.disconnect();
          } catch {
            // Already disconnected — fine.
          }
        }, 300);
      }
    } catch {
      // Best-effort teardown.
    }
  }

  /** Construct the node graph, all gains at zero, sources running. */
  private build(): SawGraph {
    const ctx = getAudioContext();
    if (ctx.state === "suspended") {
      void ctx.resume().catch(() => {});
    }
    const noise = getNoiseBuffer(ctx);
    const sources: AudioScheduledSourceNode[] = [];
    const noiseSrc = () => {
      const node = ctx.createBufferSource();
      node.buffer = noise;
      node.loop = true;
      node.start(0, Math.random() * noise.duration);
      sources.push(node);
      return node;
    };

    const master = ctx.createGain();
    master.gain.value = 0.7;
    master.connect(getRoomBus());
    // Everything dulls together as the blade buries: one lowpass door
    const lowpass = ctx.createBiquadFilter();
    lowpass.type = "lowpass";
    lowpass.frequency.value = 6500;
    lowpass.connect(master);

    // TEETH: square-chopped noise at tooth-pass rate (see machineSynth's
    // cut module — same AM construction, hand speed in place of rpm)
    const chop = ctx.createGain();
    chop.gain.value = 0.6;
    const chopDepth = ctx.createGain();
    chopDepth.gain.value = 0.4;
    const toothOsc = ctx.createOscillator();
    toothOsc.type = "square";
    toothOsc.frequency.value = 0;
    toothOsc.start();
    sources.push(toothOsc);
    toothOsc.connect(chopDepth);
    chopDepth.connect(chop.gain);
    noiseSrc().connect(chop);

    // FIBER: the chopped noise, bright with speed
    const fiberBandpass = ctx.createBiquadFilter();
    fiberBandpass.type = "bandpass";
    fiberBandpass.frequency.value = 900;
    fiberBandpass.Q.value = 0.7;
    const fiberGain = ctx.createGain();
    fiberGain.gain.value = 0;
    chop.connect(fiberBandpass).connect(fiberGain).connect(lowpass);

    // BODY: wood formants under the tearing
    const bodyGain = ctx.createGain();
    bodyGain.gain.value = 0;
    for (const formant of [
      { hz: 850, q: 3, gain: 1.0 },
      { hz: 1700, q: 4, gain: 0.55 },
      { hz: 3000, q: 5, gain: 0.35 },
    ]) {
      const bp = ctx.createBiquadFilter();
      bp.type = "bandpass";
      bp.frequency.value = formant.hz;
      bp.Q.value = formant.q;
      const g = ctx.createGain();
      g.gain.value = formant.gain;
      chop.connect(bp).connect(g).connect(bodyGain);
    }
    bodyGain.connect(lowpass);

    // BLADE: the plate's shimmer, a beating detuned pair
    const bladeGain = ctx.createGain();
    bladeGain.gain.value = 0;
    for (const hz of [2360, 2378]) {
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = hz;
      osc.start();
      sources.push(osc);
      osc.connect(bladeGain);
    }
    bladeGain.connect(lowpass);

    // RATTLE: the nearly-free offcut burring against the blade
    const rattleChop = ctx.createGain();
    rattleChop.gain.value = 0.35;
    const rattleDepth = ctx.createGain();
    rattleDepth.gain.value = 0.65;
    const rattleOsc = ctx.createOscillator();
    rattleOsc.type = "square";
    rattleOsc.frequency.value = 31;
    rattleOsc.start();
    sources.push(rattleOsc);
    rattleOsc.connect(rattleDepth);
    rattleDepth.connect(rattleChop.gain);
    noiseSrc().connect(rattleChop);
    const rattleBandpass = ctx.createBiquadFilter();
    rattleBandpass.type = "bandpass";
    rattleBandpass.frequency.value = 500;
    rattleBandpass.Q.value = 1.8;
    const rattleGain = ctx.createGain();
    rattleGain.gain.value = 0;
    rattleChop.connect(rattleBandpass).connect(rattleGain).connect(lowpass);

    return {
      master,
      lowpass,
      toothOsc,
      chopBase: chop,
      chopDepth,
      fiberBandpass,
      fiberGain,
      bodyGain,
      bladeGain,
      rattleGain,
      sources,
    };
  }

  /** The blade unloading and re-biting as the stroke turns around. */
  private reversalTick(): void {
    const graph = this.graph;
    if (!graph) return;
    const ctx = getAudioContext();
    const source = ctx.createBufferSource();
    source.buffer = getNoiseBuffer(ctx);
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 1900;
    bp.Q.value = 1.5;
    const env = ctx.createGain();
    const now = ctx.currentTime;
    const level = 0.2 * Math.min(1, this.speed / FULL_SPEED);
    env.gain.setValueAtTime(level, now);
    env.gain.exponentialRampToValueAtTime(0.001, now + 0.03);
    source.connect(bp).connect(env).connect(graph.master);
    source.start(now, Math.random(), 0.04);
  }
}

/**
 * The cut letting go: a crack as the last fibers tear, then the offcut
 * knocking against the bench as it settles. Built straight onto the
 * room bus, not the voice's graph, so the surface unmounting on
 * completion can't cut its tail off.
 */
export function playSawPartCrack(): void {
  try {
    const ctx = getAudioContext();
    if (ctx.state === "suspended") {
      void ctx.resume().catch(() => {});
    }
    const master = ctx.createGain();
    master.gain.value = 0.85;
    master.connect(getRoomBus());
    const burst = (
      at: number,
      hz: number,
      q: number,
      level: number,
      decay: number,
    ) => {
      const source = ctx.createBufferSource();
      source.buffer = getNoiseBuffer(ctx);
      const bp = ctx.createBiquadFilter();
      bp.type = "bandpass";
      bp.frequency.value = hz;
      bp.Q.value = q;
      const env = ctx.createGain();
      env.gain.setValueAtTime(level, at);
      env.gain.exponentialRampToValueAtTime(0.001, at + decay);
      source.connect(bp).connect(env).connect(master);
      source.start(at, Math.random(), decay + 0.05);
    };
    const now = ctx.currentTime;
    // The crack: fibers tearing — a snap over a low thump. Wide-ish Q:
    // a narrow band starves a short noise burst of its energy.
    burst(now, 2400, 0.9, 0.6, 0.03);
    burst(now, 700, 0.8, 1.0, 0.09);
    burst(now, 180, 1.1, 0.85, 0.13);
    // The offcut settling: two softening wood knocks
    burst(now + 0.1, 340, 2.2, 0.5, 0.07);
    burst(now + 0.22, 300, 2.2, 0.3, 0.07);
    setTimeout(() => {
      try {
        master.disconnect();
      } catch {
        // Already gone — fine.
      }
    }, 800);
  } catch {
    // Silence is the fallback.
  }
}
